import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Pipeline from "@/pages/Pipeline";
import CustomerDetail from "@/pages/CustomerDetail";
import EnquiryDetail from "@/pages/EnquiryDetail";
import FollowUps from "@/pages/FollowUps";
import CalendarPage from "@/pages/CalendarPage";
import Settings from "@/pages/Settings";
import BuildersHub from "@/pages/BuildersHub";
import BuilderJobDetail from "@/pages/BuilderJobDetail";
import AuditLog from "@/pages/AuditLog";
import Flooring from "@/pages/Flooring";
import Dispatch from "@/pages/Dispatch";
import Stock from "@/pages/Stock";
import PoImport from "@/pages/PoImport";
import StockLocations from "@/pages/StockLocations";
import PriceChecker from "@/pages/PriceChecker";
import Portfolio from "@/pages/Portfolio";
import PortfolioProject from "@/pages/PortfolioProject";
import PortfolioShare from "@/pages/PortfolioShare";
import Vessels from "@/pages/Vessels";
import VesselPublic from "@/pages/VesselPublic";
import CustomsApp from "@/pages/CustomsApp";
import BuilderPortal from "@/pages/BuilderPortal";
import Deliveries from "@/pages/Deliveries";
import DeliveryPortal from "@/pages/DeliveryPortal";
import Takeoffs from "@/pages/Takeoffs";
import EstimatorTakeoffs from "@/pages/EstimatorTakeoffs";
import SharePage from "@/pages/SharePage";
import AppUnlockGate from "@/components/AppUnlockGate";

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="app-loader">
      <Loader2 className="h-7 w-7 animate-spin text-primary" strokeWidth={2} />
    </div>
  );
}

function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (location.pathname.startsWith("/customs")) {
    return <CustomsApp />;
  }
  if (location.pathname.startsWith("/portal")) {
    return <BuilderPortal />;
  }
  if (location.pathname.startsWith("/share/") || location.pathname.startsWith("/showcase/") || location.pathname.startsWith("/ship/") || location.pathname.startsWith("/d/")) {
    return (
      <Routes>
        <Route path="/share/:token" element={<SharePage />} />
        <Route path="/showcase/c/:token" element={<PortfolioShare kind="category" />} />
        <Route path="/showcase/p/:token" element={<PortfolioShare kind="project" />} />
        <Route path="/ship/supplier/:token" element={<VesselPublic kind="supplier" />} />
        <Route path="/d/:token" element={<DeliveryPortal />} />
      </Routes>
    );
  }
  if (loading) return <FullScreenLoader />;
  if (!user) return <Login />;
  if (user.role === "warehouse") {
    return (
      <AppUnlockGate>
        <Routes>
          <Route element={<AppShell />}>
            <Route path="/dispatch" element={<Dispatch />} />
            <Route path="*" element={<Navigate to="/dispatch" replace />} />
          </Route>
        </Routes>
      </AppUnlockGate>
    );
  }
  if (user.role === "estimator") {
    return (
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/takeoffs" element={<EstimatorTakeoffs />} />
          <Route path="*" element={<Navigate to="/takeoffs" replace />} />
        </Route>
      </Routes>
    );
  }
  return (
    <AppUnlockGate>
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/builders" element={<BuildersHub />} />
        <Route path="/po-import" element={<PoImport />} />
        <Route path="/stock-locations" element={<StockLocations />} />
        <Route path="/price-check" element={<PriceChecker />} />
        <Route path="/vessels" element={<Vessels />} />
        <Route path="/vessels/:vesselId" element={<Vessels />} />
        <Route path="/takeoffs" element={<Takeoffs />} />
        <Route path="/takeoffs/:takeoffId" element={<Takeoffs />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/portfolio/:categoryId" element={<Portfolio />} />
        <Route path="/portfolio/projects/:projectId" element={<PortfolioProject />} />
        <Route path="/builders/:builderId" element={<BuildersHub />} />
        <Route path="/builder-jobs/:jobId" element={<BuilderJobDetail />} />
        <Route path="/customers/:id" element={<CustomerDetail />} />
        <Route path="/enquiries/:id" element={<EnquiryDetail />} />
        <Route path="/followups" element={<FollowUps />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/audit-log" element={<AuditLog />} />
        <Route path="/flooring" element={<Flooring />} />
        <Route path="/dispatch" element={<Dispatch />} />
        <Route path="/deliveries" element={<Deliveries />} />
        <Route path="/stock" element={<Stock />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
    </AppUnlockGate>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
          <Toaster position="top-center" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
